import { InfoOutlined } from '@mui/icons-material';
import { Box, Button, DialogActions, DialogContent, Typography } from '@mui/material';
import { Category, RiskLevel } from '@zefr/style-guide/dist/ui/types/atrium/garm';
import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';
import { demoDialogContentNodeAtom, demoDialogOpenAtom } from '../../atoms/demoDialogAtom';
import { GarmRiskVideoCounts } from '../../types/proxyApi';
import GarmScoreCard, { ScoreData } from './GarmScoreCard';

const HeaderContent = () => {
  return (
    <Box
      position={'relative'}
      px={12}
      py={8}
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
    >
      <Typography variant="h5" color="black" fontWeight={'800'} textAlign={'center'}>
        Risk Details
      </Typography>
    </Box>
  );
};

interface BodyContentProps {
  scoreData?: Partial<Record<Category, ScoreData>>;
}
const BodyContent: React.FC<BodyContentProps> = ({ scoreData }) => {
  return (
    <DialogContent
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        gap: 2,
      }}
    >
      <GarmScoreCard scoreData={scoreData} />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1 }}>
        <InfoOutlined sx={{ fontSize: '1.1rem', color: 'text.secondary', mt: '2px' }} />
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
          Risk Level is determined by the number of videos detected at each level relative to the
          overall size of the Channel. We also use LLM signals to refine the risk rating even when
          few videos surface at that level. High and Floor risk are only assigned through human
          review — a Channel may be marked High Risk even when no individual High Risk videos are
          present.
        </Typography>
      </Box>
    </DialogContent>
  );
};

const FooterContent = () => {
  const setDemoDialog = useSetAtom(demoDialogOpenAtom);
  const handleClose = useCallback(() => {
    setDemoDialog(false);
  }, [setDemoDialog]);

  return (
    <DialogActions sx={{ justifyContent: 'space-evenly' }}>
      <Button
        fullWidth={true}
        size={'large'}
        variant="contained"
        onClick={handleClose}
        color="info"
      >
        Close
      </Button>
    </DialogActions>
  );
};

interface BodyContentProps {
  scoreData?: Partial<Record<Category, ScoreData>>;
}

const useGarmScoreDialog = (
  params: Record<string, RiskLevel>,
  videoCounts?: GarmRiskVideoCounts,
) => {
  const transformedParams: Record<string, ScoreData> = {};
  Object.keys(params).forEach((p) => {
    transformedParams[p] = {
      riskLevel: params[p],
      counts: videoCounts?.[p],
    };
  });
  if (videoCounts) {
    Object.keys(videoCounts).forEach((p) => {
      if (!transformedParams[p]) {
        transformedParams[p] = { counts: videoCounts[p] };
      }
    });
  }
  const setDemoDialog = useSetAtom(demoDialogOpenAtom);
  const setDemoDialogContent = useSetAtom(demoDialogContentNodeAtom);
  const handleClick = useCallback(() => {
    setDemoDialogContent(
      <>
        <HeaderContent />
        <BodyContent scoreData={transformedParams} />
        <FooterContent />
      </>,
    );
    setDemoDialog(true);
  }, [setDemoDialog, setDemoDialogContent]);
  return handleClick;
};

export default useGarmScoreDialog;
